import { type PathParam } from "react-router";
import type { ParameterizedRoute } from "../constants/routes";
import { useAncestorRouteParams, useRouteParams } from "./useRouteParams";

// Callers provide a route descriptor so parameter names are checked at compile
// time and values are read from that route's active match at runtime.
export function useParamAsNumber<
  Path extends string,
  Params extends Readonly<Record<string, unknown>>,
  InputParams extends Readonly<Record<string, unknown>>,
  ParameterName extends PathParam<Path>,
>(
  route: ParameterizedRoute<Path, Params, InputParams>,
  parameterName: ParameterName,
): number;
export function useParamAsNumber<
  Path extends string,
  Params extends Readonly<Record<string, unknown>>,
  InputParams extends Readonly<Record<string, unknown>>,
  ParameterName extends PathParam<Path>,
>(
  route: ParameterizedRoute<Path, Params, InputParams>,
  parameterName: ParameterName,
  required: boolean,
): number | null;

export function useParamAsNumber<
  Path extends string,
  Params extends Readonly<Record<string, unknown>>,
  InputParams extends Readonly<Record<string, unknown>>,
  ParameterName extends PathParam<Path>,
>(
  route: ParameterizedRoute<Path, Params, InputParams>,
  parameterName: ParameterName,
  required?: boolean,
) {
  const params = useRouteParams(route);

  return parseNumberParam(params?.[parameterName], parameterName, required);
}

export function useAncestorParamAsNumber<
  Path extends string,
  Params extends Readonly<Record<string, unknown>>,
  InputParams extends Readonly<Record<string, unknown>>,
  ParameterName extends PathParam<Path>,
>(
  route: ParameterizedRoute<Path, Params, InputParams>,
  parameterName: ParameterName,
): number {
  const params = useAncestorRouteParams(route);
  const parameterValue = parseNumberParam(
    params?.[parameterName],
    parameterName,
  );

  if (parameterValue === null) {
    throw new Error(`Cannot find parameter ${parameterName}`);
  }

  return parameterValue;
}

function parseNumberParam(
  parameterValue: unknown,
  parameterName: string,
  required?: boolean,
): number | null {
  const isRequired = required !== false;

  if (!parameterValue && isRequired)
    throw new Error(`Cannot find parameter ${parameterName}`);

  if (!parameterValue) return null;

  if (typeof parameterValue === "number") {
    return parameterValue;
  }

  if (typeof parameterValue !== "string") {
    throw new Error(`Parameter value for ${parameterName} is not a number.`);
  }

  const parameterNumber = parseInt(parameterValue, 10);

  if (isNaN(parameterNumber))
    throw new Error(`Parameter value for ${parameterName} is not a number.`);

  return parameterNumber;
}
